import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useTenant } from "../../../../Context/Tenant/TenantContext";
import { SET_SELECTED_TENANT_ID, TOGGLE_SHOW_EDIT_TENANT_DIALOG, UPDATE_TENANT } from "../TenantEvents";
import { useState } from "react";
import ErrorMessage from "../../../Shared/Error/ErrorMessage";
import InnerLoader from "../../../Shared/Loader/InnerLoader";
import { OverlayLoader } from "../../../Shared/Loader/OverlayLoader";
import { updateTenant } from "../../../../API/Tenant/Tenant";
import { useTranslation } from "react-i18next";

const EditTenantDialog = () => {
    const { state, dispatch } = useTenant();
    const { selectedTenantId, tenants } = state;
    const selectedTenant = tenants.find(tenant => tenant._id === selectedTenantId);
    const [isSaving, setIsSaving] = useState(false);
    const [tenantName, setTenantName] = useState(selectedTenant.tenantName);
    const [validity, setValidity] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const { t } = useTranslation([
        "tenant/tenant",
    ], {
        useSuspense: false
    })

    const handleTenantNameChange = (e) => {
        const { value } = e.target;
        setTenantName(value);
        validateTenantName(value);
    };

    const validateTenantName = (name) => {
        if (name.trim().length === 0) {
            setValidity(false);
            setErrorMessage(t("tenant_name_required"));
            return;
        }
        if (isNameTaken(name)) {
            setValidity(false);
            setErrorMessage(t("tenant_name_already_exists"));
            return;
        }
        setValidity(true);
        setErrorMessage("");
    };

    const isNameTaken = (name) => {
        const filteredTenants = tenants.filter(tenant => tenant._id !== selectedTenant._id);
        return filteredTenants.some(tenant => tenant.tenantName.trim().toLowerCase() === name.trim().toLowerCase());
    };

    const handleSave = async () => {
        if (!validity) return;
        setIsSaving(true);
        try {
            const response = await updateTenant(selectedTenant._id, { tenantName: tenantName.trim() });
            if (response._id) {
                dispatch({
                    type: UPDATE_TENANT,
                    payload: {
                        id: selectedTenant._id,
                        data: { tenantName: tenantName.trim() }
                    }
                });
                handleClose();
            } else {
                throw new Error("Failed to update tenant");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        dispatch({ type: SET_SELECTED_TENANT_ID, payload: null });
        dispatch({ type: TOGGLE_SHOW_EDIT_TENANT_DIALOG });
    };

    const shouldDisableSaveButton = () => {
        return !validity || isSaving || tenantName.trim().toLowerCase() === selectedTenant.tenantName.trim().toLowerCase();
    };

    if (!selectedTenant) return null;

    return (
        <Dialog
            title={t("edit_tenant")}
            onClose={handleClose} width={500} height={200}
        >
            <form>
                {isSaving && <OverlayLoader />}
                <label className="form-label" required>
                    {t("tenant_name")}
                </label>
                {!validity && <ErrorMessage visible={!validity} error={errorMessage} />}
                <input
                    type="text"
                    className="form-control"
                    value={tenantName}
                    onChange={handleTenantNameChange}
                />
            </form>
            <DialogActionsBar layout="end">
                <div className="mb-2 me-3">
                    {isSaving && <InnerLoader borderColor="green" />}
                    <button onClick={handleClose} className="btn btn-secondary">
                        {t("cancel_button")}
                    </button>
                    <button
                        disabled={shouldDisableSaveButton()}
                        onClick={handleSave}
                        className="btn btn-primary"
                    >
                        {t("update_button")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default EditTenantDialog;